import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap, toArray } from 'rxjs';
import {
  DateTimeService,
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  AUDIT_EVENTS_MAX_TOP,
  AUDIT_EVENTS_PAGE_SIZE,
  TAuditEventFilters,
  TAuditEventPage,
  TAuditEventRow,
  TAuditEventView,
} from '../entities/audit-event.model';
import { AuditEventService } from '../infra/audit-event.service';
import { fetchAllPages } from './fetch-all-pages.util';

/**
 * Instants inside `details` whose name does not end in "Utc". Same list the
 * timeline request declares (`ProfileService.getTimelineByPeriod`).
 */
const DETAILS_UTC_FIELDS = ['neverSince'];

/** How far the export walk got. */
export type TAuditExportProgress = {
  Fetched: number;
  Total: number;
};

type TAuditEventState = {
  /** The filters of the list on screen. */
  filters: TAuditEventFilters;
  /** The page on screen, with `Details` parsed. */
  rows: TAuditEventRow[];
  /** Rows of the whole filtered query. */
  total: number;
  skip: number;
  top: number;
  /** Every row of the filtered query, for the export. */
  exportRows: TAuditEventRow[] | null;
  exportProgress: TAuditExportProgress | null;
};

const initialState: TAuditEventState = {
  filters: {},
  rows: [],
  total: 0,
  skip: 0,
  top: AUDIT_EVENTS_PAGE_SIZE,
  exportRows: null,
  exportProgress: null,
};

const OPS = ['load', 'export'] as const;

const ERRORS = {
  load: 'SETTINGS.AUDIT.ERRORS.LOAD',
  export: 'SETTINGS.AUDIT.ERRORS.EXPORT',
} as const;

/**
 * `Details` is JSON text written by the backend itself; unreadable text (which
 * the backend should never store) reads as "no details", not as a failure.
 * The result is as untyped as any JSON body — the row type gives it its shape,
 * by event.
 */
function parseDetailsJson(text: string | null) {
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** A page request: the filters and the window. */
type TAuditPageRequest = {
  filters: TAuditEventFilters;
  skip: number;
};

/**
 * Ajustes › Auditoria: the audit list (OData `AuditEvents`), paginated by
 * `$skip`/`$top`, and the export of the whole filtered query in pages of 1000.
 *
 * `Details` arrives as JSON TEXT, so the `DateInterceptor` never saw the
 * dates inside it. The store parses it once per row, when the page lands, and
 * runs it through `DateTimeService.toLocalObject` — the same, single
 * conversion the interceptor gives the timeline's `Details` object. Nothing
 * downstream converts again.
 *
 * A failed request leaves the i18n key in `loadError` / `exportError` for the
 * screen's error state, and hands the backend message to the alert through
 * `ErrorStore.setHttpError` (same split as `InactivationLogStore`).
 *
 * The export walk pages a live query: a record written while it runs pushes
 * the later pages down one row, and that row comes twice. The pages are
 * joined by `AuditEventId`, keeping the first copy.
 */
export const AuditEventStore = signalStore(
  { providedIn: 'root' },
  withState<TAuditEventState>(initialState),
  withReqState(...OPS),
  withComputed((store) => ({
    /** There are rows past the page on screen. */
    hasNextPage: computed<boolean>(
      () => store.skip() + store.rows().length < store.total()
    ),
    hasPreviousPage: computed<boolean>(() => store.skip() > 0),
  })),
  withMethods(
    (
      store,
      service = inject(AuditEventService),
      dateTime = inject(DateTimeService),
      errorStore = inject(ErrorStore)
    ) => {
      /** The backend message goes to the alert; the state keeps the key. */
      const failWith = (error: HttpErrorResponse, key: string): string => {
        errorStore.setHttpError(error);
        return key;
      };

      /** The row with `Details` parsed and its instants converted, once. */
      const toRow = (view: TAuditEventView): TAuditEventRow => {
        const details = parseDetailsJson(view.Details);
        return {
          ...view,
          ParsedDetails:
            details === null
              ? null
              : dateTime.toLocalObject(details, DETAILS_UTC_FIELDS),
        };
      };

      /** Every row once, in walk order: a row repeated by a shifted page is dropped. */
      const uniqueRows = (pages: TAuditEventPage[]): TAuditEventRow[] => {
        const seen = new Set<string>();
        const views: TAuditEventView[] = [];
        for (const view of pages.flatMap((page) => page.Items)) {
          if (!seen.has(view.AuditEventId)) {
            seen.add(view.AuditEventId);
            views.push(view);
          }
        }
        return views.map(toRow);
      };

      const load = rxMethod<TAuditPageRequest>(
        pipe(
          tap(({ filters, skip }) =>
            patchState(store, { filters, skip }, setPropLoading('load'))
          ),
          switchMap(({ filters, skip }) =>
            service.getPage(filters, skip, store.top()).pipe(
              tapResponse({
                next: (page: TAuditEventPage) =>
                  patchState(
                    store,
                    { rows: page.Items.map(toRow), total: page.Total },
                    setPropLoaded('load')
                  ),
                error: (error: HttpErrorResponse) =>
                  patchState(
                    store,
                    setPropError('load', failWith(error, ERRORS.load))
                  ),
              })
            )
          )
        )
      );

      return {
        /** Applies new filters and loads their first page. */
        search: (filters: TAuditEventFilters): void => {
          load({ filters, skip: 0 });
        },

        /** Loads the page that starts at `skip`, with the current filters. */
        loadPage: (skip: number): void => {
          load({ filters: store.filters(), skip: Math.max(0, skip) });
        },

        /** Next / previous page, with the current filters. */
        nextPage: (): void => {
          load({ filters: store.filters(), skip: store.skip() + store.top() });
        },
        previousPage: (): void => {
          load({
            filters: store.filters(),
            skip: Math.max(0, store.skip() - store.top()),
          });
        },

        /**
         * EVERY row of the current filters, for the CSV/PDF: pages of 1000
         * until `Total`. The rows land in `exportRows`; `exportProgress`
         * follows the walk.
         */
        exportAll: rxMethod<void>(
          pipe(
            tap(() =>
              patchState(
                store,
                { exportRows: null, exportProgress: { Fetched: 0, Total: 0 } },
                setPropLoading('export')
              )
            ),
            switchMap(() => {
              const filters = store.filters();
              return fetchAllPages<TAuditEventPage>((skip) =>
                service.getPage(filters, skip, AUDIT_EVENTS_MAX_TOP)
              ).pipe(
                tap((page) =>
                  patchState(store, (state) => ({
                    exportProgress: {
                      Fetched:
                        (state.exportProgress?.Fetched ?? 0) +
                        page.Items.length,
                      Total: page.Total,
                    },
                  }))
                ),
                toArray(),
                tapResponse({
                  next: (pages) =>
                    patchState(
                      store,
                      {
                        exportRows: uniqueRows(pages),
                      },
                      setPropLoaded('export')
                    ),
                  error: (error: HttpErrorResponse) =>
                    patchState(
                      store,
                      setPropError('export', failWith(error, ERRORS.export))
                    ),
                })
              );
            })
          )
        ),

        /** Drops the exported rows once the file is written. */
        clearExport: (): void =>
          patchState(
            store,
            { exportRows: null, exportProgress: null },
            setPropInit('export')
          ),

        /** Back to the untouched state (leaving the page). */
        reset: (): void => patchState(store, initialState, setPropInit(...OPS)),
      };
    }
  )
);
